import { Input, Empty } from "antd";
import { FC, useState, useMemo } from "react";
import { Package, X, CheckSquare, Square, Search, Image as ImageIcon } from "lucide-react";
import { useGetMyInventories, SortByField } from "~/services/inventory";
import { CheckType, InventoryStatusCode, SortOrder } from "~/utils/constants";
import { TInventory } from "~/types";
import { useDebounceValue } from "~/hooks/use-debounce";
import { formatDimensions, capitalize } from "~/utils";
import { InventoryPhotoHistorySidebar } from "./inventory-photo-history-sidebar";

interface InventorySelectionSidebarProps {
  selectedInventoryIds: string[];
  onSelect: (ids: string[]) => void;
  onClose: () => void;
  subClientId?: string;
}

const getStatusBadge = (status: InventoryStatusCode) => {
  const statusConfig = {
    [InventoryStatusCode.AVAILABLE_ON_HAND]: {
      text: "Available On Hand",
      className: "bg-black text-white",
    },
    [InventoryStatusCode.AVAILABLE_LIVE]: {
      text: "Available Live",
      className: "bg-black text-white",
    },
    [InventoryStatusCode.NO_POSSESSION]: {
      text: "No Possession",
      className: "bg-gray-200 text-black",
    },
    [InventoryStatusCode.RELEASED]: {
      text: "Released",
      className: "bg-gray-200 text-black",
    },
  };
  const config =
    statusConfig[status] ||
    statusConfig[InventoryStatusCode.NO_POSSESSION];
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-bold uppercase border-2 border-black ${config.className}`}
    >
      {config.text}
    </span>
  );
};

export const InventorySelectionSidebar: FC<InventorySelectionSidebarProps> = ({
  selectedInventoryIds,
  onSelect,
  onClose,
  subClientId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 300);
  const [selectedInventoryForView, setSelectedInventoryForView] = useState<TInventory | null>(null);
  const [viewMode, setViewMode] = useState<"available" | "all">("available");
  const [sortBy, setSortBy] = useState<SortByField>(SortByField.INVENTORY_REF);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);

  // Fetch inventory items based on view mode - filter by subclient if provided
  const { data: inventoryData, isLoading } = useGetMyInventories({
    checkParentId: CheckType.UNCHECK,
    status: viewMode === "available" ? InventoryStatusCode.AVAILABLE_ON_HAND : undefined,
    search,
    page: 1,
    pageSize: 100,
    sortBy,
    sortOrder,
    subClientId,
  });

  const toggleSelection = (id: string) => {
    if (selectedInventoryIds.includes(id)) {
      onSelect(selectedInventoryIds.filter((itemId) => itemId !== id));
    } else {
      onSelect([...selectedInventoryIds, id]);
    }
  };

  const toggleSelectAll = () => {
    if (!inventoryData?.items) return;
    const allIds = inventoryData.items.map((item) => item.id);
    if (selectedInventoryIds.length === allIds.length) {
      onSelect([]);
    } else {
      onSelect(allIds);
    }
  };

  const allSelected = useMemo(() => {
    if (!inventoryData?.items || inventoryData.items.length === 0) return false;
    return inventoryData.items.every((item) =>
      selectedInventoryIds.includes(item.id)
    );
  }, [inventoryData?.items, selectedInventoryIds]);

  const items = inventoryData?.items || [];

  return (
    <div className="h-full flex relative">
      <div className="h-full flex flex-col bg-[#fffff5] flex-1">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#fffff5]">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Package size={20} className="text-black flex-shrink-0" />
          <h2 className="text-lg font-bold text-black uppercase tracking-wide !mb-0">
            Select Inventory
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#fffef5] transition-colors flex-shrink-0 ml-2"
          aria-label="Close sidebar"
        >
          <X size={20} className="text-black" />
        </button>
      </div>

      {/* View Toggle and Search */}
      <div className="p-4 border-b-2 border-black space-y-3">
        {/* View Mode Toggle */}
        <div className="border-2 border-black inline-flex w-full">
          <button
            onClick={() => setViewMode("available")}
            className={`flex-1 px-4 py-2 font-bold text-xs uppercase tracking-wide border-r-2 border-black transition-all ${
              viewMode === "available"
                ? "bg-black text-white"
                : "bg-[#fffff5] text-black hover:bg-[#fffef5]"
            }`}
          >
            Available On Hand
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`flex-1 px-4 py-2 font-bold text-xs uppercase tracking-wide transition-all ${
              viewMode === "all"
                ? "bg-black text-white"
                : "bg-[#fffff5] text-black hover:bg-[#fffef5]"
            }`}
          >
            All Inventory
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black pointer-events-none"
          />
          <Input
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-2 border-black pl-10"
          />
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-black">Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortByField)}
            className="flex-1 border-2 border-black px-3 py-1.5 font-bold text-xs uppercase bg-[#fffff5]"
          >
            <option value={SortByField.INVENTORY_REF}>Ref Number</option>
            <option value={SortByField.TITLE}>Title</option>
            <option value={SortByField.ARTIST_NAME}>Artist</option>
            <option value={SortByField.CREATED_AT}>Date Created</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC)}
            className="border-2 border-black px-3 py-1.5 font-bold text-xs uppercase bg-[#fffff5] hover:bg-[#fffef5]"
          >
            {sortOrder === SortOrder.ASC ? "↑" : "↓"}
          </button>
        </div>
      </div>

      {/* Select All */}
      {items.length > 0 && (
        <div className="px-4 py-2 border-b-2 border-black">
          <button
            onClick={toggleSelectAll}
            className="w-full flex items-center gap-2 p-2 border-2 border-black bg-[#fffff5] hover:bg-[#fffef5] transition-all"
          >
            {allSelected ? (
              <CheckSquare size={18} className="text-black" />
            ) : (
              <Square size={18} className="text-black" />
            )}
            <span className="text-sm font-bold uppercase tracking-wide text-black">
              Select All ({items.length})
            </span>
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-8 text-center text-black font-bold uppercase tracking-wide">
            Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8">
            <Empty
              description={
                <span className="text-black font-bold uppercase tracking-wide text-sm">
                  No inventory items found
                </span>
              }
            />
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {items.map((item) => {
              const isSelected = selectedInventoryIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => toggleSelection(item.id)}
                  className={`w-full text-left p-3 border-2 border-black transition-all ${
                    isSelected
                      ? "bg-black text-white"
                      : "bg-[#fffff5] text-black hover:bg-[#fffef5]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {isSelected ? (
                        <CheckSquare size={18} />
                      ) : (
                        <Square size={18} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Client Ref Number */}
                      <div className="font-bold text-sm uppercase tracking-wide truncate">
                        {item.inventoryRef || "No Ref"}
                      </div>
                      {/* Client Internal Ref */}
                      {item.clientInternalRef && (
                        <div
                          className={`text-xs mt-0.5 font-medium truncate ${
                            isSelected ? "text-white" : "text-black"
                          }`}
                        >
                          Client Ref: {item.clientInternalRef}
                        </div>
                      )}
                      {/* Title */}
                      {item.title && (
                        <div
                          className={`text-xs mt-1 font-medium truncate ${
                            isSelected ? "text-white" : "text-black"
                          }`}
                        >
                          {item.title}
                        </div>
                      )}
                      {/* Status */}
                      <div className="mt-2">
                        {getStatusBadge(item.status)}
                      </div>
                    </div>
                    {/* Photo/History Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedInventoryForView(item);
                      }}
                      className={`ml-2 p-1.5 border-2 border-black transition-colors flex-shrink-0 ${
                        isSelected
                          ? "bg-white hover:bg-gray-100 text-black"
                          : "bg-[#fffff5] hover:bg-[#fffef5] text-black"
                      }`}
                      title="View photos and history"
                    >
                      <ImageIcon size={16} />
                    </button>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t-2 border-black bg-[#fffff5]">
        <div className="text-xs font-bold uppercase tracking-wide text-black mb-2">
          Selected: {selectedInventoryIds.length}
        </div>
        <button
          onClick={onClose}
          className="w-full border-2 border-black bg-black text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all"
        >
          Done
        </button>
      </div>

      </div>

      {/* Inventory Photo/History Sidebar (nested) */}
      {selectedInventoryForView && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-[#fffff5] transition-all duration-300 ease-in-out border-r-2 border-black max-w-[100vw] overflow-x-hidden ${
              selectedInventoryForView ? "w-[600px] translate-x-0" : "w-[600px] translate-x-full"
            }`}
            style={{ zIndex: 10 }}
          >
            <InventoryPhotoHistorySidebar
              inventory={selectedInventoryForView}
              onClose={() => setSelectedInventoryForView(null)}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[55] lg:hidden"
            onClick={() => setSelectedInventoryForView(null)}
          />
        </>
      )}
    </div>
  );
};

